import type { StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import { EntityBaseActions, HistoryControlActions } from '@medsurf/flat-actions';
import { EntityBaseState } from './entity.base.state';

export type HistoryEntityBaseStateModel<T extends FlatModels.Base | FlatModels.UserEntityModels.User> = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<T>;

export abstract class HistoryEntityBaseState<TEntity extends FlatModels.Base | FlatModels.UserEntityModels.User, TStateModel extends HistoryEntityBaseStateModel<TEntity>> extends EntityBaseState<TEntity, TStateModel>{
  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  override modifyEntitySuccess(state: StateContext<TStateModel>,
                               action: EntityBaseActions.ModifyEntitySuccess) {
    state.dispatch(new HistoryControlActions.ClearHistoryForEntity(action.saveSnapshotItem));
    return super.modifyEntitySuccess(state, action);
  }

  override modifyEntitiesSuccess(state: StateContext<TStateModel>,
                                 action: EntityBaseActions.ModifyEntitiesSuccess) {
    state.dispatch(new HistoryControlActions.ClearHistoryForEntities(action.saveSnapshotItems));
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  addEntity(state: StateContext<TStateModel>,
            {entity}: EntityBaseActions.AddEntity<TEntity>) {
    // Add Entity
    const entities = structuredClone(state.getState().entities);
    entities[entity.id] = entity;

    // Patch State
    state.patchState({
      entities
    } as TStateModel);
  }

  updateEntity(state: StateContext<TStateModel>,
               {entity}: EntityBaseActions.UpdateEntity<TEntity>) {
    // Update Entity
    const entities = structuredClone(state.getState().entities);
    entities[entity.id] = entity;

    // Patch State
    state.patchState({
      entities
    } as TStateModel);
  }

  override removeEntity(state: StateContext<TStateModel>,
                        {entity}: EntityBaseActions.RemoveEntity<TEntity>) {
    // Remove Entity
    const entities = structuredClone(state.getState().entities);
    delete entities[entity.id];

    // Patch State
    state.patchState({
      entities
    } as TStateModel);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  undoSnapshot(state: StateContext<TStateModel>,
               {snapshotItem}: EntityBaseActions.UndoSnapshot) {
    // Check Modifier
    switch (snapshotItem.modifierType) {
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD:
        this.#undoSnapshotAdd(state, snapshotItem);
        break;
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE:
        this.#undoSnapshotUpdate(state, snapshotItem);
        break;
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE:
        // Uses the same logic as snapshot update
        this.#undoSnapshotUpdate(state, snapshotItem);
        break;
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          "notification.type_not_supported",
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.HistoryEntityBaseState.undoSnapshot"
        );
    }
  }
  
  #undoSnapshotAdd(state: StateContext<TStateModel>,
                   snapshotItem: FlatModels.EntityControlModels.HistorySnapshotItem) {
    // Get entities and revert snapshot
    const entities = structuredClone(state.getState().entities);
    delete entities[snapshotItem.entityId];

    // Patch State
    state.patchState({
      entities
    } as TStateModel);
  }

  #undoSnapshotUpdate(state: StateContext<TStateModel>,
                      snapshotItem: FlatModels.EntityControlModels.HistorySnapshotItem) {
    if (!snapshotItem.previousSnapshotId) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        "notification.no_previous_snapshot_found",
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.HistoryEntityBaseState.#undoSnapshotUpdate"
      );
    }

    // Get snapshot
    const snapshots = structuredClone(state.getState().snapshots);
    const snapshot = snapshots[snapshotItem.previousSnapshotId];

    if (!snapshot) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        "notification.no_snapshot_found",
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.HistoryEntityBaseState.#undoSnapshotUpdate"
      );
    }

    // Parse Entity
    const entity = JSON.parse(snapshot.itemJson);

    // Get entities and revert snapshot
    const entities = structuredClone(state.getState().entities);
    entities[entity.id] = entity;

    // Patch State
    state.patchState({
      entities
    } as TStateModel);
  }

  redoSnapshot(state: StateContext<TStateModel>,
               {snapshotItem}: EntityBaseActions.RedoSnapshot) {
    // Check Modifier
    switch (snapshotItem.modifierType) {
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD:
        this.#redoSnapshotUpdate(state, snapshotItem);
        break;
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE:
        this.#redoSnapshotUpdate(state, snapshotItem);
        break;
      case FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE:
        // Uses the same logic as snapshot update
        this.#redoSnapshotRemove(state, snapshotItem);
        break;
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          "notification.type_not_supported",
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.HistoryEntityBaseState.redoSnapshot"
        );
    }
  }

  #redoSnapshotUpdate(state: StateContext<TStateModel>,
                      snapshotItem: FlatModels.EntityControlModels.HistorySnapshotItem) {
    if (!snapshotItem.followingSnapshotId) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        "notification.no_following_snapshot_found",
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.HistoryEntityBaseState.#redoSnapshotUpdate"
      );
    }

    // Get snapshot
    const snapshots = structuredClone(state.getState().snapshots);
    const snapshot = snapshots[snapshotItem.followingSnapshotId];

    if (!snapshot) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        "notification.no_snapshot_found",
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.HistoryEntityBaseState.#redoSnapshotUpdate"
      );
    }

    // Parse Entity
    const entity = JSON.parse(snapshot.itemJson);

    // Get entities and revert snapshot
    const entities = structuredClone(state.getState().entities);
    entities[entity.id] = entity;

    // Patch State
    state.patchState({
      entities
    } as TStateModel);
  }

  #redoSnapshotRemove(state: StateContext<TStateModel>,
                      snapshotItem: FlatModels.EntityControlModels.HistorySnapshotItem) {
    // Get entities and revert snapshot
    const entities = structuredClone(state.getState().entities);
    delete entities[snapshotItem.entityId];

    // Patch State
    state.patchState({
      entities
    } as TStateModel);
  }

  cleanUpSnapshots(state: StateContext<TStateModel>,
                   {snapshotIds}: EntityBaseActions.CleanUpSnapshots) {
    // Get snapshots
    const snapshots = structuredClone(state.getState().snapshots);
    for (const snapshotId of snapshotIds) {
      delete snapshots[snapshotId];
    }

    // Patch State
    state.patchState({
      snapshots
    } as TStateModel);
  }

  //</editor-fold>

  //</editor-fold>
}
